import { useState, useRef, useEffect } from "react";
import { Box } from "@mantine/core";
import { TimePicker } from "@mantine/dates";

/**
 * TimePicker with smart dropdown behavior:
 * - Closes when user clicks a preset (mouse selection)
 * - Stays open when user types (keyboard) to avoid closing mid-input
 * - Closes when user clicks outside (handled by Popover)
 */
interface TimePickerWithSmartDropdownProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  error?: string;
  minutesStep?: number;
  presets: Array<{ label: string; values: string[] }> | string[];
  [key: string]: unknown;
}

export function TimePickerWithSmartDropdown({
  value,
  onChange,
  label,
  required = false,
  error,
  minutesStep = 15,
  presets,
  ...rest
}: TimePickerWithSmartDropdownProps) {
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const lastInteractionRef = useRef<"mouse" | "keyboard" | null>(null);

  useEffect(() => {
    if (!dropdownOpened) return;
    lastInteractionRef.current = null;
    const onMouseDown = () => {
      lastInteractionRef.current = "mouse";
    };
    const onKeyDown = () => {
      lastInteractionRef.current = "keyboard";
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [dropdownOpened]);

  const handleChange = (newValue: string) => {
    onChange(newValue);
    // Only close on preset click (mouse), not when typing (keyboard)
    if (newValue && lastInteractionRef.current === "mouse") {
      setDropdownOpened(false);
    }
  };

  return (
    <Box onClickCapture={() => setDropdownOpened(true)}>
      <TimePicker
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={() => setDropdownOpened(true)}
        format="12h"
        withSeconds={false}
        minutesStep={minutesStep}
        presets={presets}
        withDropdown
        popoverProps={{
          width: "target",
          opened: dropdownOpened,
          onChange: (opened) => !opened && setDropdownOpened(false),
        }}
        maxDropdownContentHeight={300}
        required={required}
        error={error}
        styles={(theme) => ({
          input: {
            fontSize: "1rem",
            "&:focus": {
              borderColor: theme.colors.teal[5],
            },
          },
        })}
        {...rest}
      />
    </Box>
  );
}
